// Stepping the scan target in and out one level, without opening the picker.
//
// The picker is the right tool for "file this somewhere else entirely" and the wrong one for
// "actually it's the drawer below this one": it covers the camera, resets your place in the tree
// and costs several taps to end up one node away. These two rules back the < and > arrows in the
// scan header, which is the cheap move.
//
// Both take the flat locations array the client already holds — `id`, `name`,
// `parent_location_id`, `last_used_at` — and return a location id or null. Null means the arrow
// has nowhere to go and renders disabled; neither rule ever invents a target.

function byId(locations, id) {
  return (locations || []).find((l) => l.id === id) || null;
}

// Out: the parent. Null at a top-level location, which is the top of the user's world — there is
// no "everything" node above it to land on.
export function outwardTarget(locations, locationId) {
  const loc = byId(locations, locationId);
  if (!loc || !loc.parent_location_id) return null;
  // A parent id that no longer resolves (deleted, or outside what this user can see) is not a
  // place to send the camera.
  return byId(locations, loc.parent_location_id) ? loc.parent_location_id : null;
}

// In: the ONE sub-location below this one, when there is only one.
//
// Ambiguity is the whole reason this is restrictive. With three drawers below the dresser, any
// choice the arrow makes is a guess, and it makes it silently while the header reports the result
// as fact — which is exactly the failure that took firstScanTarget three attempts to stop making
// (see the comment there). An arrow that does nothing is a much smaller problem than one that
// quietly files a box of screws in the wrong drawer, and the picker is one tap away and shows the
// whole tree.
//
// So: exactly one child means step into it, more than one means the arrow is dead and the user
// picks. "Most recently used" was tried here first and is a genuinely good guess, but a good guess
// presented as a certainty is the thing being removed.
export function inwardTarget(locations, locationId) {
  // No destination yet — "Add item" from home — has no level below it. Without this the filter
  // below matches on null and quietly treats the top-level locations as children of nowhere, so the
  // arrow worked for a user with one house and was dead for a user with two. The first choice of
  // where to put something belongs in the picker either way.
  if (!locationId) return null;
  const kids = (locations || []).filter((l) => l.parent_location_id === locationId);
  return kids.length === 1 ? kids[0].id : null;
}
